import { useState, useRef, useEffect, useCallback } from "react";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDropdownOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setDropdownOpen(false), 200);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  const mainArticles = articles.slice(0, 6);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <a
              href="/"
              className="flex items-center gap-2 group"
            >
              <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight ${scrolled ? "text-foreground-950" : "text-background-50"}`}>
                hipobuyspread<span className="text-primary-400">.</span>beer
              </span>
            </a>

            <div className="flex items-center gap-3 md:gap-5">
              <div
                ref={dropdownRef}
                className="relative hidden md:block"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    scrolled
                      ? "text-foreground-700 hover:text-foreground-950 bg-background-100 hover:bg-background-200"
                      : "text-background-50/90 hover:text-background-50 bg-background-50/10 hover:bg-background-50/20"
                  }`}
                >
                  <span>Browse Categories</span>
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-80 overflow-y-auto"
                    style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}
                  >
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base text-primary-500`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`hidden md:inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "text-background-50 bg-foreground-950 hover:bg-foreground-800"
                    : "text-foreground-950 bg-background-50 hover:bg-background-100"
                }`}
              >
                hipobuy sheet
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>

              <button
                onClick={() => setMobileOpen(true)}
                className={`md:hidden flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-200 cursor-pointer ${
                  scrolled
                    ? "text-foreground-950 hover:bg-background-100"
                    : "text-background-50 hover:bg-background-50/10"
                }`}
                aria-label="Open menu"
              >
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={closeMobile}
        ></div>

        <div
          className={`absolute top-0 right-0 w-full max-w-sm h-full bg-background-50 transition-transform duration-300 ease-out ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
            <a href="/" onClick={closeMobile} className="font-heading text-lg font-bold text-foreground-950 tracking-tight">
              hipobuyspread<span className="text-primary-500">.</span>beer
            </a>
            <button
              onClick={closeMobile}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-xl text-foreground-700"></i>
            </button>
          </div>

          <div className="overflow-y-auto h-[calc(100%-65px)]">
            <div className="px-5 pt-5 pb-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 w-full px-5 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer mb-3"
              >
                hipobuy sheet
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 w-full px-5 py-3 text-sm font-semibold text-foreground-950 bg-background-100 hover:bg-background-200 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                buy it now
                <i className="ri-shopping-bag-line text-base"></i>
              </a>
            </div>

            <div className="px-5 py-4">
              <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3 whitespace-nowrap">
                Product Categories
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl bg-background-100/80 hover:bg-background-200/80 border border-background-200/40 transition-all duration-150 cursor-pointer"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className={`${cat.icon} text-sm text-primary-600`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800 leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-background-200/60">
              <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3 whitespace-nowrap">
                Guides & Resources
              </h4>
              <div className="space-y-1">
                {mainArticles.map((article) => (
                  <a
                    key={article.id}
                    href={`/${article.slug}`}
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 flex-shrink-0">
                      <i className="ri-article-line text-xs text-accent-600"></i>
                    </span>
                    <span className="text-sm text-foreground-700 leading-tight line-clamp-1">
                      {article.title}
                    </span>
                  </a>
                ))}
                <a
                  href="/"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 flex-shrink-0">
                    <i className="ri-home-line text-xs text-accent-600"></i>
                  </span>
                  <span className="text-sm font-semibold text-primary-600">
                    View All Guides →
                  </span>
                </a>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-background-200/60">
              <p className="text-xs text-foreground-400 text-center">
                &copy; 2026 hipobuyspread.beer
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}